import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PortalChart } from "@/components/portal/charts/PortalChart";
import { evolutionRadarOption } from "./aiEvolution";
import {
  activityCostOption,
  categoryBreakdownOption,
  cumulativeDeployments,
  deploymentGrowthOption,
  roiTrendOption,
  topAgentsForChart,
  topAgentsOption,
  userAdoptionOption,
} from "./dashboardCharts";
import { fmt, fmtCurrency, fmtDate } from "./dashboardFormat";
import { SignCue } from "@/components/portal/apps/reporting/custom/SignCue";
import {
  agenticDashboardQueries,
  type DashboardAgent,
  type DashboardAgentTimeline,
  type DashboardEscalations,
  type DashboardEvaluatorQuality,
  type DashboardOverview,
  type DashboardSkills,
  type DashboardPeriod,
  type DashboardUserTrend,
} from "@/services/api/automation";
import {
  platformApi,
  type OrganisationUser,
} from "@/services/api/platform";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { isSuperAdmin } from "@/lib/roles";
import {
  computeEvolutionAxes,
  evolutionScore,
} from "./aiEvolution";
import {
  getAgentHealth,
  HEALTH_FILTERS,
  HEALTH_META,
  isStale,
  passRateTone,
  RATE_TONE_BAR,
  STALE_AFTER_DAYS,
  STALE_META,
} from "./agentHealth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertCircle,
  Bot,
  TrendingUp,
  Users,
  CheckCircle2,
  Zap,
  DollarSign,
  ArrowUpRight,
  Calendar,
  ShieldAlert,
  BadgeCheck,
  Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/portal/automation/dashboard")({
  component: AgenticDashboard,
});

// ============================================================================
// Helpers
// ============================================================================

const CATEGORY_COLORS: Record<string, string> = {
  marketing: "bg-purple-100 text-purple-800",
  customer_support: "bg-blue-100 text-blue-800",
  data: "bg-green-100 text-green-800",
  operations: "bg-orange-100 text-orange-800",
  finance: "bg-yellow-100 text-yellow-800",
  uncategorised: "bg-gray-100 text-gray-800",
};

function fmtPct(num: number) {
  return `${(num * 100).toFixed(1)}%`;
}

// For small per-unit figures (e.g. cost per run) where 2dp would round to $0.00.
function fmtCurrencyPrecise(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount || 0);
}

function getOrgDisplayName(org: {
  organisationName?: string;
  organisationId: string;
  data?: { CompanyName?: string };
}) {
  return org.data?.CompanyName || org.organisationName || org.organisationId;
}

function getCategoryBadgeClass(category: string) {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.uncategorised;
}

function getUserDisplayName(
  userId: string | null,
  userMap: Map<string, OrganisationUser>,
): string {
  if (!userId) return "-";
  const user = userMap.get(userId.toLowerCase());
  if (!user) return "-";
  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user.email;
}

function fmtRelativeDate(d: string | null): string {
  if (!d) return "Never";
  // The backend serialises naive-UTC ISO strings (no offset); treat as UTC so
  // Today/Yesterday aren't skewed by the viewer's timezone. Bucket by calendar day.
  const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(d) ? d : `${d}Z`;
  const then = new Date(iso);
  const startOfThen = new Date(then);
  startOfThen.setHours(0, 0, 0, 0);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const days = Math.round(
    (startOfToday.getTime() - startOfThen.getTime()) / 86_400_000,
  );
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}

// ============================================================================
// Main Component
// ============================================================================

function AgenticDashboard() {
  const { token, session } = useAuth();
  const { selectedOrganisation, organisations } = usePortalData();

  const [period, setPeriod] = useState<DashboardPeriod>("month");
  const [viewingOrgId, setViewingOrgId] = useState<string>("");
  const [healthFilter, setHealthFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Initialize viewingOrgId from selectedOrganisation
  useEffect(() => {
    if (selectedOrganisation && !viewingOrgId) {
      setViewingOrgId(selectedOrganisation.organisationId);
    }
  }, [selectedOrganisation, viewingOrgId]);

  // Access gate — SuperAdmin only. Evaluated as a flag (not an early return) so all
  // hooks below run unconditionally; the restricted view is rendered from the return.
  const canView = isSuperAdmin(session);

  // Main query — all dashboard data in one call
  const { data, isLoading } = useQuery({
    ...agenticDashboardQueries.dashboardOverview(token!, period, viewingOrgId),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: canView && !!token && !!viewingOrgId,
  });

  // Org members — for resolving primary_user_id to display names
  const { data: orgMembers } = useQuery({
    queryKey: ["org-members", viewingOrgId],
    queryFn: () => platformApi.getOrganisationUsers(token!, viewingOrgId),
    staleTime: 300_000,
    enabled: canView && !!token && !!viewingOrgId,
  });

  // Build lookup map (lowercase IDs for case-insensitive matching)
  const userMap = useMemo(() => {
    const map = new Map<string, OrganisationUser>();
    orgMembers?.forEach((u) => map.set(u.id.toLowerCase(), u));
    return map;
  }, [orgMembers]);

  // Sorted org list for the picker
  const sortedOrgs = useMemo(
    () =>
      [...organisations].sort((a, b) =>
        getOrgDisplayName(a).localeCompare(getOrgDisplayName(b)),
      ),
    [organisations],
  );

  // Filtered agents for the table
  const filteredAgents = useMemo(() => {
    if (!data?.agents) return [];
    return data.agents.filter((a) => {
      // "stale" is an extra arm on the Health dropdown, not an AgentHealth value: it is a
      // separate axis, so it is matched here rather than by getAgentHealth. An agent can be
      // both failing and stale; picking "stale" shows it, picking "failing" also shows it.
      if (healthFilter === "stale") {
        if (!isStale(a)) return false;
      } else if (healthFilter !== "all" && getAgentHealth(a) !== healthFilter)
        return false;
      if (categoryFilter !== "all" && a.category !== categoryFilter)
        return false;
      return true;
    });
  }, [data?.agents, healthFilter, categoryFilter]);

  // Registered agents with no runs in the period — the "sitting idle" signal.
  const idleAgents = useMemo(
    () => (data?.agents ?? []).filter((a) => getAgentHealth(a) === "idle"),
    [data?.agents],
  );

  // Unique categories for filter dropdown
  const categories = useMemo(() => {
    if (!data?.agents) return [];
    return [...new Set(data.agents.map((a) => a.category))].sort();
  }, [data?.agents]);

  if (!canView) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground mt-2">
              The AI Agent Dashboard is available to platform administrators
              only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <PageHeader
          title="AI Agent Dashboard"
          description="Executive overview of AI agent deployment, adoption, and ROI."
          icon={Bot}
          eyebrow="Automation & AI"
          gradient
        />
        <div className="flex gap-2 flex-shrink-0">
          {/* Org picker */}
          <Select value={viewingOrgId} onValueChange={setViewingOrgId}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Select organisation" />
            </SelectTrigger>
            <SelectContent>
              {sortedOrgs.map((org) => (
                <SelectItem
                  key={org.organisationId}
                  value={org.organisationId}
                >
                  {getOrgDisplayName(org)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Period selector */}
          <Select
            value={period}
            onValueChange={(v: DashboardPeriod) => setPeriod(v)}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Last 24h</SelectItem>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : data ? (
        <>
          {/* Section 1: Top-Line Metrics */}
          <TopLineMetrics data={data} />

          {/* Section 1b: ROI headline — benefit vs cost */}
          <RoiHeadline data={data} />

          {/* Section 2: Deployed Agents Overview */}
          <TopAgentsChart agents={data.agents} />
          <DeployedAgentsTable
            agents={filteredAgents}
            categories={categories}
            healthFilter={healthFilter}
            categoryFilter={categoryFilter}
            onHealthChange={setHealthFilter}
            onCategoryChange={setCategoryFilter}
            userMap={userMap}
          />
          <IdleAgentsCallout agents={idleAgents} />

          {/* Section 3: Reliability — escalations + evaluator quality */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <EscalationsPanel escalations={data.escalations} />
            <EvaluatorQualityPanel evaluators={data.evaluator_quality} />
          </div>

          {/* Section 3c: AI Evolution maturity */}
          <AiEvolutionRadar data={data} />

          {/* Section 3d: Skills firing */}
          {data.skills && <SkillsFiringPanel skills={data.skills} />}

          {/* Section 4: Category Coverage */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CategoryBreakdown
              categories={data.categories}
              dataKey="total_runs"
              title="Activity by Category"
              description="Distribution of agent runs across categories"
              valueLabel="runs"
            />
            <CategoryBreakdown
              categories={data.categories}
              dataKey="unique_users"
              title="Users by Category"
              description="Unique users per agent category"
              valueLabel="users"
            />
          </div>

          {/* Section 5: Deployment Timeline */}
          <DeploymentTimeline timeline={data.agents_timeline} />

          {/* Section 6: Trends & ROI */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UserAdoptionTrend trend={data.user_trend} />
            <ActivityTrend trend={data.daily_trend} />
          </div>
          <RoiMetrics data={data} />
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No data available. Select an organisation with agent activity.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Section 1: Top-Line Metrics (5 KPI cards)
// ============================================================================

function TopLineMetrics({ data }: { data: DashboardOverview }) {
  const kpis = [
    {
      title: "Active Agents",
      value: fmt(data.total_active_agents),
      icon: Bot,
      detail: `${data.total_categories} categories`,
    },
    {
      title: "Total Runs",
      value: fmt(data.total_runs),
      icon: TrendingUp,
      detail: `${data.period_days}-day period`,
    },
    {
      title: "Total Users",
      value: fmt(data.total_users),
      icon: Users,
      detail: `${data.agents.filter((a) => a.runs > 0).length} agents active`,
    },
    {
      title: "Agent Autonomy",
      value: data.autonomy_rate != null ? fmtPct(data.autonomy_rate) : "—",
      icon: CheckCircle2,
      detail:
        data.autonomy_rate != null
          ? `${fmt(data.escalations.workflow_escalations)} escalation${data.escalations.workflow_escalations !== 1 ? "s" : ""} to humans`
          : "no workflow activity",
    },
    {
      title: "Deploy Velocity",
      value:
        data.deployment_velocity_days != null
          ? `${data.deployment_velocity_days}d`
          : "N/A",
      icon: Zap,
      detail: "avg days to first run",
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {kpis.map((kpi) => (
        <Card key={kpi.title}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {kpi.title}
              </CardTitle>
              <kpi.icon className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold">{kpi.value}</span>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{kpi.detail}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ============================================================================
// Section 1b: ROI Headline (benefit-vs-cost tracker + net-value hero)
// ============================================================================

function RoiHeadline({ data }: { data: DashboardOverview }) {
  const hasBenefit =
    data.total_benefit > 0 || data.benefit_trend.some((p) => p.benefit > 0);

  const option = useMemo(() => roiTrendOption(data.benefit_trend), [data.benefit_trend]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI ROI Tracker</CardTitle>
        <CardDescription>
          Estimated business value delivered vs LLM cost
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasBenefit ? (
          <p className="text-center text-muted-foreground py-8">
            No benefit configured yet — set a per-agent value estimate to see ROI.
          </p>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-xs text-muted-foreground">Net value</p>
                <p className="text-3xl font-bold">
                  <SignCue value={data.net_value} text={fmtCurrency(data.net_value)} />
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Benefit</p>
                <p className="text-3xl font-bold">
                  {fmtCurrency(data.total_benefit)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Cost</p>
                <p className="text-3xl font-bold">
                  {fmtCurrency(data.total_cost)}
                </p>
              </div>
            </div>
            <div data-chart="AI ROI trend" className="h-[240px] w-full">
              <PortalChart option={option} ariaLabel="AI ROI trend" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 2a: Top Agents by Runs (horizontal bar chart)
// ============================================================================

function TopAgentsChart({ agents }: { agents: DashboardAgent[] }) {
  const topAgents = topAgentsForChart(agents);
  const option = useMemo(() => topAgentsOption(agents), [agents]);

  if (topAgents.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Agents by Runs</CardTitle>
          <CardDescription>
            No agent activity in the selected period.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Agents by Runs</CardTitle>
        <CardDescription>
          Most active agents in the selected period
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          data-chart="Top agents by runs"
          className="w-full"
          style={{ height: Math.max(200, topAgents.length * 36) }}
        >
          <PortalChart option={option} ariaLabel="Top agents by runs" />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 2b: Deployed Agents Table (with Primary User)
// ============================================================================

function DeployedAgentsTable({
  agents,
  categories,
  healthFilter,
  categoryFilter,
  onHealthChange,
  onCategoryChange,
  userMap,
}: {
  agents: DashboardAgent[];
  categories: string[];
  healthFilter: string;
  categoryFilter: string;
  onHealthChange: (v: string) => void;
  onCategoryChange: (v: string) => void;
  userMap: Map<string, OrganisationUser>;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Deployed Agents</CardTitle>
            <CardDescription>
              {agents.length} agent{agents.length !== 1 ? "s" : ""} shown
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={healthFilter} onValueChange={onHealthChange}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Health" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Health</SelectItem>
                {HEALTH_FILTERS.map((h) => (
                  <SelectItem key={h} value={h}>
                    {HEALTH_META[h].label}
                  </SelectItem>
                ))}
                {/* Separate axis, listed last so it reads as an extra lens rather than a
                    fifth mutually-exclusive health state. */}
                <SelectItem value="stale">{STALE_META.label}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={onCategoryChange}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {agents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium">Agent Name</th>
                  <th className="pb-2 font-medium">Category</th>
                  <th className="pb-2 font-medium">Health</th>
                  <th className="pb-2 font-medium text-right">Success</th>
                  <th className="pb-2 font-medium text-right hidden md:table-cell">
                    Benefit
                  </th>
                  <th className="pb-2 font-medium text-right hidden lg:table-cell">
                    Eval
                  </th>
                  <th className="pb-2 font-medium hidden md:table-cell">
                    Last Run
                  </th>
                  <th className="pb-2 font-medium hidden md:table-cell">
                    Last Updated
                  </th>
                  <th className="pb-2 font-medium">Primary User</th>
                  <th className="pb-2 font-medium text-right">Runs</th>
                  <th className="pb-2 font-medium hidden lg:table-cell">
                    Function
                  </th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => {
                  const health = getAgentHealth(agent);
                  const stale = isStale(agent);
                  return (
                    <tr
                      key={agent.agent_type}
                      className="border-b last:border-0"
                    >
                      <td className="py-2.5 font-medium">{agent.name}</td>
                      <td className="py-2.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeClass(agent.category)}`}
                        >
                          {agent.category.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="py-2.5">
                        <div className="flex flex-wrap items-center gap-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${HEALTH_META[health].className}`}
                          >
                            {HEALTH_META[health].label}
                          </span>
                          {/* A SECOND badge, not a replacement: staleness is orthogonal to
                              health, so a failing-and-stale agent must still read Failing. */}
                          {stale && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STALE_META.className}`}
                              title={`Definition unchanged for ${STALE_AFTER_DAYS}+ days`}
                            >
                              {STALE_META.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 text-right">
                        {agent.runs > 0 ? fmtPct(agent.success_rate) : "-"}
                      </td>
                      <td className="py-2.5 text-right hidden md:table-cell">
                        {agent.estimated_benefit > 0
                          ? fmtCurrency(agent.estimated_benefit)
                          : "—"}
                      </td>
                      <td className="py-2.5 text-right text-muted-foreground hidden lg:table-cell">
                        {agent.eval_pass_rate != null
                          ? `${fmtPct(agent.eval_pass_rate)} (${fmt(agent.eval_count)})`
                          : "—"}
                      </td>
                      <td className="py-2.5 text-muted-foreground hidden md:table-cell">
                        {fmtRelativeDate(agent.last_run_at)}
                      </td>
                      <td className="py-2.5 text-muted-foreground hidden md:table-cell">
                        {/* "Unknown", not fmtRelativeDate's "Never": a missing created_at
                            means we cannot date the definition, not that it was never set. */}
                        {agent.created_at ? (
                          <span className="whitespace-nowrap">
                            {fmtRelativeDate(agent.created_at)}
                            <span className="ml-1 text-xs opacity-70">
                              v{agent.version}
                            </span>
                          </span>
                        ) : (
                          "Unknown"
                        )}
                      </td>
                      <td className="py-2.5 text-muted-foreground">
                        {getUserDisplayName(agent.primary_user_id, userMap)}
                      </td>
                      <td className="py-2.5 text-right font-medium">
                        {fmt(agent.runs)}
                      </td>
                      <td className="py-2.5 text-muted-foreground text-xs hidden lg:table-cell max-w-xs truncate">
                        {agent.description || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            No agents match the selected filters.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 2c: Idle Agents Callout (registered but 0 runs in the period)
// ============================================================================

function IdleAgentsCallout({ agents }: { agents: DashboardAgent[] }) {
  if (agents.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <CardTitle className="text-base">
            {agents.length} idle agent{agents.length !== 1 ? "s" : ""}
          </CardTitle>
        </div>
        <CardDescription>
          Deployed but with no runs in the selected period — candidates to drive
          adoption or retire.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {agents.map((agent) => (
            <span
              key={agent.agent_type}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs"
            >
              {agent.name}
              <span
                className={`inline-flex items-center px-1.5 rounded text-[10px] font-medium ${getCategoryBadgeClass(agent.category)}`}
              >
                {agent.category.replace(/_/g, " ")}
              </span>
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 3a: Escalations panel (human-in-the-loop signal)
// ============================================================================

function EscalationsPanel({
  escalations,
}: {
  escalations: DashboardEscalations;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <CardTitle>Escalations to Humans</CardTitle>
        </div>
        <CardDescription>
          How often workflows hand off to a person
        </CardDescription>
      </CardHeader>
      <CardContent>
        {escalations.total_workflows === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No workflow activity in the selected period.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold">
                {fmtPct(escalations.rate)}
              </span>
              <span className="text-sm text-muted-foreground">
                {fmt(escalations.workflow_escalations)} of{" "}
                {fmt(escalations.total_workflows)} workflow
                {escalations.total_workflows !== 1 ? "s" : ""} escalated
              </span>
            </div>
            {escalations.top_reasons.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2">
                  Top reasons
                </h4>
                <ul className="space-y-1.5">
                  {escalations.top_reasons.map((r) => (
                    <li
                      key={r.reason}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="truncate">{r.reason}</span>
                      <span className="font-medium text-muted-foreground shrink-0">
                        {fmt(r.count)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 3b: Evaluator quality panel (are the agents producing good output?)
// ============================================================================

function EvaluatorQualityPanel({
  evaluators,
}: {
  evaluators: DashboardEvaluatorQuality[];
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <BadgeCheck className="h-4 w-4 text-muted-foreground" />
          <CardTitle>Evaluator Quality</CardTitle>
        </div>
        <CardDescription>
          Pass rate and score per quality evaluator
        </CardDescription>
      </CardHeader>
      <CardContent>
        {evaluators.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No evaluations in the selected period.
          </p>
        ) : (
          <ul className="space-y-3">
            {evaluators.map((e) => (
              <li key={e.evaluator} className="space-y-1">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium truncate">
                    {e.evaluator.replace(/_/g, " ")}
                  </span>
                  <span className="text-muted-foreground shrink-0">
                    {fmtPct(e.pass_rate)} pass
                    {e.avg_score != null
                      ? ` · ${e.avg_score.toFixed(2)} avg`
                      : ""}{" "}
                    · {fmt(e.total)} eval{e.total !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full ${RATE_TONE_BAR[passRateTone(e.pass_rate)]}`}
                    style={{ width: `${Math.round(e.pass_rate * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 3d: Skills Firing (top skills + never-fired dead weight)
// ============================================================================

function SkillsFiringPanel({ skills }: { skills: DashboardSkills }) {
  const maxCount = skills.top.length > 0 ? skills.top[0].count : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <CardTitle>Top Skills Fired</CardTitle>
          </div>
          <CardDescription>
            {fmt(skills.total_invocations)} skill invocation
            {skills.total_invocations !== 1 ? "s" : ""} in the period
          </CardDescription>
        </CardHeader>
        <CardContent>
          {skills.top.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No skill activity in the selected period.
            </p>
          ) : (
            <ul className="space-y-2">
              {skills.top.map((s) => (
                <li key={s.skill} className="space-y-1">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate font-medium">{s.skill}</span>
                    <span className="text-muted-foreground shrink-0">
                      {fmt(s.count)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{
                        width: `${maxCount > 0 ? Math.round((s.count / maxCount) * 100) : 0}%`,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            <CardTitle>Never Fired</CardTitle>
          </div>
          <CardDescription>
            {skills.total_invocations === 0
              ? "No skill telemetry captured in this period yet"
              : "Configured skills with no invocation in the period — dead weight to prune"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {skills.never_fired.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Every configured skill fired at least once.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {skills.never_fired.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs text-muted-foreground"
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Section 3c: AI Evolution Score (maturity radar)
// ============================================================================

function AiEvolutionRadar({ data }: { data: DashboardOverview }) {
  const axes = useMemo(() => computeEvolutionAxes(data), [data]);
  const score = Math.round(evolutionScore(axes) * 100);
  const option = useMemo(() => evolutionRadarOption(axes), [axes]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>AI Evolution Score</CardTitle>
            <CardDescription>
              Maturity across autonomy, quality, reliability, adoption and coverage
            </CardDescription>
          </div>
          <p className="text-3xl font-bold whitespace-nowrap">
            {score}
            <span className="text-base font-normal text-muted-foreground">
              /100
            </span>
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div data-chart="AI Evolution Score radar" className="h-[280px]">
            <PortalChart option={option} ariaLabel="AI Evolution Score radar" />
          </div>
          <ul className="space-y-2">
            {axes.map((a) => (
              <li
                key={a.key}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span>{a.label}</span>
                <span className="font-medium text-muted-foreground">
                  {a.hasData ? `${Math.round(a.value * 100)}%` : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 4: Category Coverage (pie charts — reusable for runs + users)
// ============================================================================

function CategoryBreakdown({
  categories,
  dataKey,
  title,
  description,
  valueLabel,
}: {
  categories: DashboardOverview["categories"];
  dataKey: "total_runs" | "unique_users";
  title: string;
  description: string;
  valueLabel: string;
}) {
  const entries = Object.entries(categories);
  const option = useMemo(
    () => categoryBreakdownOption(categories, dataKey, valueLabel),
    [categories, dataKey, valueLabel],
  );
  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            No category data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div data-chart={title} className="h-[280px]">
          <PortalChart option={option} ariaLabel={title} />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 5: Deployment Timeline
// ============================================================================

function DeploymentTimeline({
  timeline,
}: {
  timeline: DashboardAgentTimeline[];
}) {
  // Cumulative agent count over time
  const agentsWithDates = timeline.filter((a) => a.created_at);
  const cumulativeData = useMemo(() => cumulativeDeployments(timeline), [timeline]);
  const option = useMemo(() => deploymentGrowthOption(timeline), [timeline]);

  // Most recently created agents (last 5)
  const recentAgents = [...agentsWithDates]
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent Deployment Growth</CardTitle>
        <CardDescription>
          Cumulative agent deployments over time
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {cumulativeData.length > 0 ? (
          <div data-chart="Agent deployment growth" className="h-[250px]">
            <PortalChart option={option} ariaLabel="Agent deployment growth" />
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-4">
            No deployment date data available.
          </p>
        )}

        {recentAgents.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-3">
              Recently Deployed Agents
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {recentAgents.map((agent) => (
                <div
                  key={agent.agent_type}
                  className="border rounded-lg p-3 space-y-1"
                >
                  <p className="font-medium text-sm truncate">{agent.name}</p>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeClass(agent.category)}`}
                  >
                    {agent.category.replace(/_/g, " ")}
                  </span>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {agent.created_at ? fmtDate(agent.created_at) : "N/A"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 6a: User Adoption Trend (area chart)
// ============================================================================

function UserAdoptionTrend({ trend }: { trend: DashboardUserTrend[] }) {
  const option = useMemo(() => userAdoptionOption(trend ?? []), [trend]);
  if (!trend || trend.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Adoption Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            No user adoption data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Adoption Trend</CardTitle>
        <CardDescription>
          Cumulative and daily active users over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div data-chart="User adoption trend" className="h-[280px]">
          <PortalChart option={option} ariaLabel="User adoption trend" />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 6b: Activity + Cost Trend (area chart)
// ============================================================================

function ActivityTrend({
  trend,
}: {
  trend: DashboardOverview["daily_trend"];
}) {
  const option = useMemo(() => activityCostOption(trend ?? []), [trend]);
  if (!trend || trend.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity & Cost Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            No trend data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity & Cost Trend</CardTitle>
        <CardDescription>Agent runs and cost over the period</CardDescription>
      </CardHeader>
      <CardContent>
        <div data-chart="Activity and cost trend" className="h-[280px]">
          <PortalChart option={option} ariaLabel="Activity and cost trend" />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Section 6c: ROI Metrics (KPI cards)
// ============================================================================

function RoiMetrics({ data }: { data: DashboardOverview }) {
  const costPerRun =
    data.total_runs > 0 ? data.total_cost / data.total_runs : 0;
  const hasBenefit = data.total_benefit > 0;

  const kpis = [
    {
      title: "Net Value",
      value: hasBenefit ? fmtCurrency(data.net_value) : "—",
      signed: hasBenefit ? data.net_value : undefined,
      icon: TrendingUp,
      detail: hasBenefit ? "benefit − cost" : "no benefit configured",
    },
    {
      title: "Total Benefit",
      value: hasBenefit ? fmtCurrency(data.total_benefit) : "—",
      icon: DollarSign,
      detail: hasBenefit ? "estimated value delivered" : "no benefit configured",
    },
    {
      title: "Total Cost",
      value: fmtCurrency(data.total_cost),
      icon: DollarSign,
      detail: `${data.period_days}-day LLM spend`,
    },
    {
      title: "Cost per Run",
      value: fmtCurrencyPrecise(costPerRun),
      icon: DollarSign,
      detail: "LLM efficiency",
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {kpis.map((kpi) => (
        <Card key={kpi.title}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {kpi.title}
              </CardTitle>
              <kpi.icon className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold">
              {kpi.signed === undefined ? (
                kpi.value
              ) : (
                <SignCue value={kpi.signed} text={kpi.value} />
              )}
            </span>
            <p className="text-xs text-muted-foreground mt-1">{kpi.detail}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
